import Button from "@ui/button/Button"
import { useState } from "react"
import { toast } from "react-toastify"
import { systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { T } from "../adminTheme"
import { AdminModal } from "./AdminModal"
import { Callout, Check, DangerZone, Fields, Muted, Row } from "./adminUi"

const EMAIL_TAKEN = 2805
const tidy = (value: string) => value.trim().toLowerCase()
const looksLikeEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 50

/** Superadmin_User_Email_Change_FE.md: the e-mail is the login, so the change lives in a danger zone behind a red modal. */
export const UserEmailChange = ({ token, userUid, email, onChanged }: { token: string; userUid: string; email: string; onChanged: () => Promise<void> | void }) => {
	const [open, setOpen] = useState(false)
	return <DangerZone title='Change e-mail' hint='The e-mail is the login. Use to fix a typo or to free an address taken by a duplicate account.'>
		<Row><Button variant='dangerSmall' onClick={() => setOpen(true)}>Change e-mail</Button><Muted style={{ fontSize: 13 }}>Current: {email}</Muted></Row>
		{open && <EmailChangeModal token={token} userUid={userUid} email={email} onClose={() => setOpen(false)} onChanged={onChanged} />}
	</DangerZone>
}

const EmailChangeModal = ({ token, userUid, email, onClose, onChanged }: { token: string; userUid: string; email: string; onClose: () => void; onChanged: () => Promise<void> | void }) => {
	const [next, setNext] = useState("")
	const [repeat, setRepeat] = useState("")
	const [understood, setUnderstood] = useState(false)
	const [busy, setBusy] = useState(false)
	const [taken, setTaken] = useState("")
	const [error, setError] = useState("")
	const value = tidy(next)
	const problem = !value ? "" : !looksLikeEmail(value) ? "Enter a valid e-mail of at most 50 characters." : value === tidy(email) ? "This is the current e-mail." : repeat && tidy(repeat) !== value ? "The two e-mails do not match." : ""
	const ready = !!value && !problem && tidy(repeat) === value && understood

	const save = async () => {
		setBusy(true); setTaken(""); setError("")
		const response = await systemPost<{ email?: string; password_resets_voided?: number | null }>(token, "system/user/email/change", { user_uid: userUid, email, new_email: value })
		setBusy(false)
		if (response.error) {
			if (Number(response.error.code) === EMAIL_TAKEN) setTaken(String(response.error.details || "This e-mail is already registered. Choose another one."))
			else setError(String(response.error.details || systemErrorMessage(response.error)))
			return
		}
		const voided = response.data?.password_resets_voided
		toast.success(`E-mail changed to ${response.data?.email ?? value}. Sessions closed${voided === null ? "; old reset links could not be voided" : voided ? `, ${voided} reset link(s) voided` : ""}.`)
		onClose()
		await onChanged()
	}

	return <AdminModal title='Change e-mail' subtitle={`User ${userUid}`} width={560} onClose={onClose}>
		<Callout tone='danger' title='Dangerous. The e-mail is the user’s login.'>
			<ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
				<li>They can only log in with the new e-mail; all their current sessions close.</li>
				<li>Password reset links sent to the old address stop working.</li>
				<li>The new address counts as confirmed at once — the user does not confirm it, so check it for typos.</li>
				<li>Card issuers (holder data) keep the old e-mail.</li>
			</ul>
			<div style={{ marginTop: 6 }}>It can only be undone by another change.</div>
		</Callout>
		<Fields style={{ gridTemplateColumns: "1fr" }}>
			<label>Current e-mail<input value={email} readOnly style={{ background: T.hover, color: T.text2, boxShadow: "none" }} /></label>
			<label>New e-mail<input type='email' autoComplete='off' maxLength={50} value={next} onChange={(event) => { setNext(event.target.value); setTaken("") }} aria-invalid={!!(problem || taken)} /></label>
			<label>Repeat new e-mail<input type='email' autoComplete='off' maxLength={50} value={repeat} onChange={(event) => setRepeat(event.target.value)} onPaste={(event) => event.preventDefault()} /></label>
		</Fields>
		{(problem || taken) && <Callout tone='danger'>{taken || problem}</Callout>}
		{error && <Callout tone='danger'>{error}</Callout>}
		<Check checked={understood} onChange={(event) => setUnderstood(event.target.checked)}>I understand the consequences</Check>
		<Row style={{ justifyContent: "flex-end" }}>
			<Button variant='secondarySmall' onClick={onClose}>Cancel</Button>
			<Button variant='dangerSmall' disabled={!ready || busy} onClick={() => void save()}>{busy ? "Changing…" : "Change e-mail"}</Button>
		</Row>
	</AdminModal>
}
